import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Header() {
  const { itemCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/search?q=${encodeURIComponent(searchQuery)}`);
        const data = await res.json();
        setSearchResults(data);
        setShowResults(true);
      } catch (err) {
        console.error('Search error:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleResultClick = (slug) => {
    setShowResults(false);
    setSearchQuery('');
    navigate(`/pricelist/${slug}`);
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return 'active';
    if (path !== '/' && location.pathname.startsWith(path)) return 'active';
    return '';
  };

  return (
    <header className="site-header" data-testid="site-header">
      <div className="header-top">
        <Link to="/" className="header-logo" data-testid="header-logo">
          <img src="/kovar_logo.png" alt="Kovar Analytics" className="logo-icon" style={{ width: '170px', height: 'auto' }} />
        </Link>

        <div className="header-search" ref={searchRef} data-testid="header-search">
          <input
            type="text"
            placeholder="Search tests and services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
          />
          <Search className="search-icon" size={20} />
          {showResults && searchResults.length > 0 && (
            <div className="header-search-results" data-testid="search-results">
              {searchResults.map((product) => (
                <div
                  key={product._id}
                  className="search-result-item"
                  onClick={() => handleResultClick(product.slug)}
                  data-testid={`search-result-${product.slug}`}
                >
                  <img src={product.image_url} alt={product.name} />
                  <div className="result-info">
                    <div className="result-name">{product.name}</div>
                    <div className="result-price">{product.price} $</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="header-actions">
          <Link to="/cart" className="cart-btn" data-testid="cart-btn">
            <ShoppingBag size={18} />
            <span>+{itemCount}</span>
          </Link>
          <Link to="/account" className="user-btn" data-testid="user-btn">
            <User size={20} />
          </Link>
        </div>
      </div>

      <nav className="header-nav" data-testid="header-nav">
        <ul>
          <li><Link to="/" className={isActive('/')}>Home</Link></li>
          <li><Link to="/services" className={isActive('/services')}>Services</Link></li>
          <li><Link to="/verification" className={isActive('/verification')}>Verify</Link></li>
          <li><Link to="/about-us" className={isActive('/about-us')}>About Us</Link></li>
          <li><Link to="/#contact-us" onClick={(e) => { if (location.pathname === '/') { e.preventDefault(); document.getElementById('contact-us')?.scrollIntoView({ behavior: 'smooth' }); }}}>Contact Us</Link></li>
        </ul>
      </nav>
    </header>
  );
}

export default Header;
